import { type ReactNode, useContext, useState } from 'react';
import { Box, Flex, HStack, IconButton, Spinner, Text } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { ENGLISH_LANGUAGE_CODE } from '@duke-hq/shared-constants/courseTranslationLanguages';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import {
  ConversationFlowPreview,
  isFlowContent,
} from '@duke-hq/app-admin-web/UnitsOfCompetency/Preview/LearningMaterialPreview';
import { useReadyByLanguage } from '../../translations/useReadyByLanguage';
import { FlowEditorStateContext } from '../FlowEditorStateContext';
import { SimulationTiptap } from './SimulationTiptap';
import { FileUrlsProvider } from '@duke-hq/svc-files-ui/useTiptapFileSrcResolver';

// The panel shell around the translated preview, matching SimulationTiptap's own
// chrome (left border, bg.subtle surface, top-right close button, and the
// container/white-space resets that keep the conversation rendering correctly in
// the narrow canvas panel). Without this the translated view would drop the
// panel's background and close control.
function CanvasPanelShell({ children }: { children: ReactNode }) {
  const { simulationButtonProps } = useContext(FlowEditorStateContext);
  return (
    <Flex as="section" h="100%" borderLeftWidth="1px">
      <Flex flex="1" minH="0" bg="bg.subtle" flexDir="column">
        <HStack px="2" py="2" justifyContent="flex-end" alignItems="center">
          <IconButton
            {...simulationButtonProps()}
            variant="ghost"
            size="sm"
            aria-label="close preview"
          >
            <X size="16px" color="currentColor" />
          </IconButton>
        </HStack>
        <Flex
          flex="1 0 0"
          flexDir="column"
          w="full"
          overflowY="auto"
          overflowX="hidden"
          gap={2}
          p={4}
          alignItems="flex-start"
          alignSelf="stretch"
          bg="bg.muted"
          whiteSpace="normal"
          sx={{
            containerType: 'inline-size',
            '& .tiptap': { marginLeft: 0 },
            '& .tiptap > * + *': { marginTop: '0.25rem' },
          }}
        >
          {children}
        </Flex>
      </Flex>
    </Flex>
  );
}

// The Flow Editor's in-canvas preview panel with a reading-language picker that
// mirrors the student switcher.
//
// English previews the LIVE canvas (SimulationTiptap traverses the editable
// graph) — the author's working state. Picking a language previews the PUBLISHED
// translation instead, exactly what a learner is served (SimulationTiptap never
// sees a published version, and a student never sees unsaved edits), so the two
// are deliberately different sources. When a language has no translation yet the
// published English flow is shown, matching the student's silent fallback.
export function CanvasSimulationPanel({
  lastPublishedVersionId,
}: {
  lastPublishedVersionId: number | null;
}) {
  const [language, setLanguage] = useState<string>(ENGLISH_LANGUAGE_CODE);
  const isTranslated = language !== ENGLISH_LANGUAGE_CODE && lastPublishedVersionId !== null;

  // Availability per language, so the picker can grey out ones that aren't ready.
  const readyByLanguage = useReadyByLanguage(lastPublishedVersionId, 'CONVERSATION', true);

  const languageControls = { value: language, onChange: setLanguage, readyByLanguage };

  const translatedQuery = trpcAdmin.contentTranslations.getTranslatedContent.useQuery(
    { publishedVersionId: lastPublishedVersionId ?? 0, resourceKind: 'CONVERSATION', language },
    { enabled: isTranslated },
  );
  const publishedQuery = trpcAdmin.resourceVersions.byId.useQuery(lastPublishedVersionId ?? 0, {
    enabled: isTranslated,
  });

  if (!isTranslated) {
    return <SimulationTiptap language={languageControls} />;
  }

  if (translatedQuery.isLoading || publishedQuery.isLoading) {
    return (
      <CanvasPanelShell>
        <Flex flex="1" w="full" justify="center" align="center" minH={pxToRem(200)}>
          <Spinner />
        </Flex>
      </CanvasPanelShell>
    );
  }

  // Null content means "serve English", so fall back to the published flow.
  const content = translatedQuery.data?.content ?? publishedQuery.data?.content;
  if (!isFlowContent(content)) {
    return (
      <CanvasPanelShell>
        <Box w="full" textAlign="center" py="8">
          <Text color="fg.muted">
            <Trans>No conversation content available.</Trans>
          </Text>
        </Box>
      </CanvasPanelShell>
    );
  }

  return (
    <CanvasPanelShell>
      <Box w="full">
        <FileUrlsProvider
          fileUrls={
            translatedQuery.data?.content != null
              ? translatedQuery.data.fileUrls
              : publishedQuery.data?.fileUrls
          }
        >
          <ConversationFlowPreview content={content} language={languageControls} maxHeight="none" />
        </FileUrlsProvider>
      </Box>
    </CanvasPanelShell>
  );
}
